import { get } from 'lodash'
import {
	deleteReq, getReq, patchReq, postReq, putReq
} from '../../utils/request'
import {
	ManagerRecurringTaskTemplate,
	ManagerRecurringTaskTemplateFilters,
	ManagerRecurringTaskTemplatePayload,
	ManagerTask,
	ManagerTaskFilters,
	ManagerTaskLabel,
	ManagerTaskListResponse,
	ManagerTaskPayload
} from './types'

const compactParams = (params: Record<string, unknown>) => Object.keys(params)
	.reduce((payload: Record<string, unknown>, key) => {
		if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
			payload[key] = params[key]
		}
		return payload
	}, {})

export const getManagerTasks = async (filters: ManagerTaskFilters = {}): Promise<ManagerTaskListResponse> => {
	const { data } = await getReq('/api/admin/manager-tasks', compactParams({
		limit: 100,
		page: 1,
		...filters
	}))

	return {
		data: get(data, 'data', []) as ManagerTask[],
		meta: get(data, 'meta', { total: 0, page: 1, limit: 100 })
	}
}

export const getManagerTask = async (id: number): Promise<ManagerTask> => {
	const { data } = await getReq(`/api/admin/manager-tasks/${id}`)
	return data as ManagerTask
}

export const createManagerTask = async (payload: ManagerTaskPayload): Promise<ManagerTask> => {
	const { data } = await postReq('/api/admin/manager-tasks', null, payload)
	return data as ManagerTask
}

export const updateManagerTask = async (id: number, payload: Partial<ManagerTaskPayload>): Promise<ManagerTask> => {
	const { data } = await putReq(`/api/admin/manager-tasks/${id}`, null, payload)
	return data as ManagerTask
}

export const completeManagerTask = async (id: number): Promise<ManagerTask> => {
	const { data } = await postReq(`/api/admin/manager-tasks/${id}/complete`, null, {})
	return data as ManagerTask
}

export const deleteManagerTask = async (id: number): Promise<void> => {
	await deleteReq(`/api/admin/manager-tasks/${id}`)
}

export const addManagerTaskComment = async (id: number, body: string): Promise<ManagerTask> => {
	const { data } = await postReq(`/api/admin/manager-tasks/${id}/comments`, null, { body })
	return data as ManagerTask
}

export const generateRecurringManagerTasks = async (): Promise<void> => {
	await postReq('/api/admin/manager-tasks/generate-recurring', null, {})
}

export const markOverdueManagerTasks = async (): Promise<void> => {
	await postReq('/api/admin/manager-tasks/mark-overdue', null, {})
}

export const getManagerTaskLabels = async (): Promise<ManagerTaskLabel[]> => {
	const { data } = await getReq('/api/admin/manager-task-labels')
	return data as ManagerTaskLabel[]
}

export const createManagerTaskLabel = async (payload: Pick<ManagerTaskLabel, 'name' | 'color' | 'linkedEntityType'>): Promise<ManagerTaskLabel> => {
	const { data } = await postReq('/api/admin/manager-task-labels', null, payload)
	return data as ManagerTaskLabel
}

export const updateManagerTaskLabel = async (
	id: number,
	payload: Pick<ManagerTaskLabel, 'name' | 'color'>
): Promise<ManagerTaskLabel> => {
	const { data } = await putReq(`/api/admin/manager-task-labels/${id}`, null, payload)
	return data as ManagerTaskLabel
}

export const deleteManagerTaskLabel = async (id: number): Promise<void> => {
	await deleteReq(`/api/admin/manager-task-labels/${id}`)
}

export const assignManagerTaskLabelEntity = async (
	taskId: number,
	labelId: number,
	entityPayload: Record<string, number | string | null>
): Promise<void> => {
	await patchReq(`/api/admin/manager-tasks/${taskId}/labels/${labelId}`, null, entityPayload)
}

export interface CalendarGhostEvent {
	id: string
	templateId: number
	occurrenceDate: string
	occurrenceKey: string
	title: string
	timeWindowFrom: string | null
	timeWindowTo: string | null
	priority: string
	isGhost: true
}

export interface CalendarMonthData {
	tasks: import('./types').ManagerTask[]
	ghosts: CalendarGhostEvent[]
}

export const getManagerTasksCalendar = async (month: string): Promise<CalendarMonthData> => {
	const { data } = await getReq('/api/admin/manager-tasks/calendar', { month })
	return data as CalendarMonthData
}

export const getRecurringTaskTemplates = async (
	filters: ManagerRecurringTaskTemplateFilters = {}
): Promise<ManagerRecurringTaskTemplate[]> => {
	const { data } = await getReq('/api/admin/manager-recurring-task-templates', compactParams(filters))
	return data as ManagerRecurringTaskTemplate[]
}

export const createRecurringTaskTemplate = async (
	payload: ManagerRecurringTaskTemplatePayload
): Promise<ManagerRecurringTaskTemplate> => {
	const { data } = await postReq('/api/admin/manager-recurring-task-templates', null, payload)
	return data as ManagerRecurringTaskTemplate
}

export const updateRecurringTaskTemplate = async (id: number, payload: Partial<ManagerRecurringTaskTemplatePayload>): Promise<ManagerRecurringTaskTemplate> => {
	const { data } = await putReq(`/api/admin/manager-recurring-task-templates/${id}`, null, payload)
	return data as ManagerRecurringTaskTemplate
}

export const deactivateRecurringTaskTemplate = async (id: number): Promise<void> => {
	await deleteReq(`/api/admin/manager-recurring-task-templates/${id}`)
}
